import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BundleBookingModal = ({ isOpen, onClose, load, onConfirm, noTrucksAvailable }) => {
  if (!isOpen || !load) return null;

  const loadCost = load?.rate * load?.quantity;
  const transportationCost = (load?.transportationRatePerTon || 1500) * load?.quantity;
  const totalBundlePrice = loadCost + transportationCost;
  const tokenAmount = Math.round(totalBundlePrice * 0.30);
  const remainingAmount = totalBundlePrice - tokenAmount;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary via-primary to-secondary text-primary-foreground p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Icon name="Package2" size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold">SmartLoad Bundle</h3>
                  <span className="px-2 py-0.5 bg-white/20 rounded text-xs font-semibold">AI Powered</span>
                </div>
                <p className="text-sm text-primary-foreground/80">Order ID: {load?.offerId}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* No Trucks Warning */}
          {noTrucksAvailable && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={22} className="text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
                    No Trucks Currently Available
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Our SmartLoad system will automatically match available trucks within 24 hours. You can proceed with booking and we'll notify you when trucks are assigned.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Load Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Product</span>
              <span className="text-sm font-medium text-foreground">{load?.product}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Quantity</span>
              <span className="text-sm font-medium text-foreground">{load?.quantity} MT</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Mill</span>
              <span className="text-sm font-medium text-foreground">{load?.millName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Location</span>
              <span className="text-sm font-medium text-foreground">{load?.deliveryFrom}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Rate per MT</span>
              <span className="text-sm font-medium text-foreground">₹{load?.rate?.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Bundle Features */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="Sparkles" size={18} className="text-primary" />
              <span className="font-semibold">Bundle Includes</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center gap-2">
                <Icon name="Package" size={20} className="text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Sugar Load</p>
                  <p className="text-sm font-semibold text-foreground">{load?.quantity} MT</p>
                </div>
              </div>
              <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-3 flex items-center gap-2">
                <Icon name="Truck" size={20} className="text-secondary" />
                <div>
                  <p className="text-xs text-muted-foreground">Trucks</p>
                  <p className="text-sm font-semibold text-foreground">{load?.trucksAvailable || 'Pending'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* AI Matching Info */}
          {!noTrucksAvailable && (
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Cpu" size={22} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">AI-Powered Truck Matching</p>
                  <p className="text-xs text-muted-foreground">
                    {load?.trucksAvailable} verified trucks available. Our AI will optimize route, cost, and delivery time for your load.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="CreditCard" size={18} className="text-primary" />
              <span className="font-semibold">Payment Details</span>
            </div>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Load Cost</span>
                <span className="text-sm font-medium text-foreground">₹{loadCost?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Transportation Cost</span>
                <span className="text-sm font-medium text-foreground">₹{transportationCost?.toLocaleString('en-IN')}</span>
              </div>
              <div className="h-px bg-border"></div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Total Bundle Price</span>
                <span className="text-lg font-bold text-orange-600">₹{totalBundlePrice?.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="FileText" size={18} className="text-primary" />
              <span className="font-semibold">Payment Terms</span>
            </div>
            <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Advance Payment at Booking</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ₹{tokenAmount?.toLocaleString('en-IN')} — secured until delivery confirmation
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Remaining 70% After Invoice</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ₹{remainingAmount?.toLocaleString('en-IN')} — due after invoice generation
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="AlertCircle" size={14} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Full Payment Before Dispatch</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Full payment required before vehicle leaves factory gate
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Secure Payment Note */}
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="ShieldCheck" size={20} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-800 dark:text-green-300 leading-relaxed">
                100% Secure Factory-Direct Transactions. Payment on delivery confirmation — funds released only after loading confirmation.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 rounded-b-xl">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={onConfirm}
              iconName="ArrowRight"
              iconPosition="right"
              fullWidth
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleBookingModal;